import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  Theme,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

interface ThemeContextType {
  mode: "light" | "dark";
  toggleTheme: () => void;
  theme: Theme;
  setThemeDirection: (isRTL: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<"light" | "dark">(() => {
    const savedMode = localStorage.getItem("aitools-theme");
    return (savedMode as "light" | "dark") || "light";
  });

  const [direction, setDirection] = useState<"ltr" | "rtl">(() => {
    const savedLang = localStorage.getItem("language");
    return savedLang === "ar" ? "rtl" : "ltr";
  });

  const theme = createTheme({
    direction,
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h3: {
        fontWeight: 700,
        letterSpacing: "-0.02em",
      },
      h5: {
        fontWeight: 600,
        letterSpacing: "-0.01em",
      },
      subtitle1: {
        fontWeight: 600,
      },
      button: {
        textTransform: "none",
        fontWeight: 600,
      },
    },
    palette: {
      mode,
      primary: {
        main: mode === "dark" ? "#FFB06A" : "#FF8428",
        light: mode === "dark" ? "#FFD0A3" : "#FFB06A",
        dark: mode === "dark" ? "#D96718" : "#D95F13",
        contrastText: mode === "dark" ? "#1B1511" : "#FFFFFF",
      },
      secondary: {
        main: mode === "dark" ? "#E8D4C0" : "#6F5A4C",
        contrastText: mode === "dark" ? "#1B1511" : "#FFFFFF",
      },
      background: {
        default: mode === "dark" ? "#171210" : "#F3E8DC",
        paper: mode === "dark" ? "#241B17" : "#FFF8EF",
      },
      text: {
        primary: mode === "dark" ? "#FFF8EF" : "#171615",
        secondary: mode === "dark" ? "#D6C2AE" : "#71665D",
      },
      success: {
        main: "#10B981", // Emerald
      },
      error: {
        main: "#EF4444", // Red
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            boxShadow:
              mode === "dark"
                ? "0 4px 6px -1px rgb(0 0 0 / 0.5), 0 2px 4px -2px rgb(0 0 0 / 0.5)"
                : "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
            transition: "box-shadow 0.2s ease-in-out",
            border: mode === "dark" ? "1px solid #334155" : "1px solid #E2E8F0",
          },
          outlined: {
            boxShadow: "none",
            border: mode === "dark" ? "1px solid #334155" : "1px solid #E2E8F0",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            border: mode === "dark" ? "1px solid #334155" : "1px solid #E2E8F0",
            boxShadow:
              mode === "dark"
                ? "0 4px 6px -1px rgb(0 0 0 / 0.5), 0 2px 4px -2px rgb(0 0 0 / 0.5)"
                : "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: "8px 16px",
            boxShadow: "none",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              boxShadow:
                mode === "dark"
                  ? "0 4px 12px rgba(129, 140, 248, 0.2)"
                  : "0 4px 12px rgba(79, 70, 229, 0.15)",
            },
          },
          contained: {
            "&:hover": {
              transform: "translateY(-1px)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
              transition: "all 0.2s ease-in-out",
              backgroundColor: mode === "dark" ? "#0F172A" : "#FFFFFF",
              "& fieldset": {
                borderColor: mode === "dark" ? "#334155" : "#CBD5E1",
              },
              "&:hover fieldset": {
                borderColor: mode === "dark" ? "#475569" : "#94A3B8",
              },
              "&.Mui-focused fieldset": {
                borderColor: mode === "dark" ? "#818CF8" : "#4F46E5",
              },
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h3: {
            color: mode === "dark" ? "#F8FAFC" : "#0F172A",
          },
        },
      },
    },
  });

  const toggleTheme = () => {
    const newMode = mode === "light" ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("aitools-theme", newMode);
  };

  useEffect(() => {
    localStorage.setItem("aitools-theme", mode);
    document.body.dataset.theme = mode;
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const setThemeDirection = (isRTL: boolean) => {
    setDirection(isRTL ? "rtl" : "ltr");
  };

  const value: ThemeContextType = {
    mode,
    toggleTheme,
    theme,
    setThemeDirection,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
